/*
 * (C) 2025 BeyondDemise, All Rights Reserved.
 */

/**
 * Clear an element of all its children. Generic over the element subtype so
 * `clearElement(myDiv)` returns the same `HTMLDivElement` rather than a widened
 * `Element`.
 */
export const clearElement = <T extends Element>(element: T): T => {
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
  return element;
};

/**
 * Safely add an event listener to an element only if it hasn't been added before.
 * Uses a `data-{listenerId}-listener-attached` attribute to track attachment, so
 * repeated calls with the same `listenerId` are no-ops.
 *
 * @returns `true` if the listener was attached, `false` if a previous call had
 *   already attached one for this `listenerId`.
 */
export const addEventListenerOnce = (
  element: Element,
  eventType: string,
  listener: EventListenerOrEventListenerObject,
  listenerId: string,
  options: boolean | AddEventListenerOptions = {}
): boolean => {
  const attributeName = `data-${listenerId}-listener-attached`;

  if (!element.hasAttribute(attributeName)) {
    element.addEventListener(eventType, listener, options);
    element.setAttribute(attributeName, 'true');
    return true;
  }
  return false;
};
